import { CommonModule, isPlatformBrowser, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/fr';
import localeId from '@angular/common/locales/id';
import { APP_ID, APP_INITIALIZER, Inject, Injector, NgModule, PLATFORM_ID } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import { authInitializer } from '@banyan/commons/initializers/auth.initializer';
import { AuthInterceptor } from '@banyan/commons/interceptors/auth.interceptor';
import { ErrorServerInterceptor } from '@banyan/commons/interceptors/error-server.interceptor';
import { LoaderInterceptor } from '@banyan/commons/interceptors/loader.interceptor';
import { LocaleInterceptor } from '@banyan/commons/interceptors/locale.interceptor';
import { AccountService } from '@banyan/modules/account/services/account.service';
import { MatIconRegistryService } from '@banyan/modules/shared/services/mat-icon-registry/mat-icon-registry.service';
import { ErrorServerModalComponent } from '@banyan/modules/standalone/error-server-modal/error-server-modal.component';
import { environment } from '@env/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CustomTitleStrategy } from './custom-title-strategy.service';
import { AdminModule } from './modules/admin/admin.module';
import { FooterModule } from './modules/footer/footer.module';
import { HeaderModule } from './modules/header/header.module';
import { SharedModule } from './modules/shared/shared.module';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

registerLocaleData(localeEn);
registerLocaleData(localeId);

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: environment.allowedUrls,
        sendAccessToken: true,
        customUrlValidation: (url: string) => {
          return !(
            !url.includes(environment.middlewareUrl)
          );
        },
      },
    }),
    AppRoutingModule,
    TranslateModule.forRoot({
      useDefaultLang: false,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    HeaderModule,
    FooterModule,
    SharedModule,
    MatSnackBarModule,
    ErrorServerModalComponent,
    GoogleTagManagerModule.forRoot({
      id: environment.googleTagManagerId,
      gtm_auth: environment.gtmAuth,
      gtm_preview: environment.gtmPreview,
    }),
    AdminModule,
  ],
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    {
      provide: APP_INITIALIZER,
      useFactory: authInitializer,
      multi: true,
      deps: [Injector, OAuthService, AccountService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorServerInterceptor,
      multi: true,
    },
    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy,
    },
    MatIconRegistry,
    MatIconRegistryService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(@Inject(PLATFORM_ID) private readonly platformId: any,
              private readonly matIconRegistryService: MatIconRegistryService) {
    if (isPlatformBrowser(this.platformId)) {
      this.matIconRegistryService.registerIcons();
    }
  }
}
